import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";

export type Tier = "starter" | "beta_free" | "pro" | "elite";

interface TierContextValue {
  tier: Tier;
  loadingTier: boolean;
  beta_started_at: string | null;
  beta_expires_at: string | null;
  refreshTier: () => Promise<void>;
}

const TierContext = createContext<TierContextValue | undefined>(undefined);

function normalizeTier(value: unknown): Tier | null {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase();
  if (v === "starter" || v === "beta_free" || v === "pro" || v === "elite") return v;
  return null;
}

function normalizeIsoString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  return v ? v : null;
}

async function loadProfileTier(userId: string): Promise<{ tier: Tier; beta_started_at: string | null; beta_expires_at: string | null }> {
  const { data, error, status } = await supabase
    .from("profiles")
    .select("tier, subscription_status, beta_started_at, beta_expires_at")
    .eq("id", userId)
    .single();

  if (!error && data) {
    return {
      tier: normalizeTier((data as any).tier) ?? "starter",
      beta_started_at: normalizeIsoString((data as any).beta_started_at),
      beta_expires_at: normalizeIsoString((data as any).beta_expires_at),
    };
  }

  const noRows = status === 406 || (error as any)?.code === "PGRST116" || (error as any)?.details?.includes?.("0 rows");
  if (noRows) {
    const { data: created, error: insertError } = await supabase
      .from("profiles")
      .insert({ id: userId, tier: "starter", subscription_status: "active", beta_started_at: null, beta_expires_at: null })
      .select("tier, beta_started_at, beta_expires_at")
      .single();

    if (insertError) {
      if (import.meta.env.DEV) {
        console.log("[tier] insert profile error", insertError);
      }
      return { tier: "starter", beta_started_at: null, beta_expires_at: null };
    }

    return {
      tier: normalizeTier((created as any)?.tier) ?? "starter",
      beta_started_at: normalizeIsoString((created as any)?.beta_started_at),
      beta_expires_at: normalizeIsoString((created as any)?.beta_expires_at),
    };
  }

  if (import.meta.env.DEV) {
    console.log("[tier] load profile error", error);
  }
  return { tier: "starter", beta_started_at: null, beta_expires_at: null };
}

export const TierProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [tier, setTier] = useState<Tier>("starter");
  const [loadingTier, setLoadingTier] = useState(true);
  const [beta_started_at, setBetaStartedAt] = useState<string | null>(null);
  const [beta_expires_at, setBetaExpiresAt] = useState<string | null>(null);

  const resetTier = useCallback(() => {
    setTier("starter");
    setBetaStartedAt(null);
    setBetaExpiresAt(null);
  }, []);

  const refreshTier = useCallback(async () => {
    if (authLoading) return;

    const userId = user?.id ?? null;
    if (!userId) {
      resetTier();
      setLoadingTier(false);
      return;
    }

    setLoadingTier(true);
    try {
      const resolved = await loadProfileTier(userId);
      setTier(resolved.tier);
      setBetaStartedAt(resolved.beta_started_at);
      setBetaExpiresAt(resolved.beta_expires_at);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.log("[tier] refreshTier error", error);
      }
      resetTier();
    } finally {
      setLoadingTier(false);
    }
  }, [authLoading, resetTier, user?.id]);

  useEffect(() => {
    if (authLoading) return;
    void refreshTier();
  }, [authLoading, refreshTier, user?.id]);

  const value = useMemo<TierContextValue>(() => {
    return { tier, loadingTier, beta_started_at, beta_expires_at, refreshTier };
  }, [tier, loadingTier, beta_started_at, beta_expires_at, refreshTier]);

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
};

export function useTier(): TierContextValue {
  const ctx = useContext(TierContext);
  if (!ctx) {
    throw new Error("useTier must be used within a TierProvider");
  }
  return ctx;
}
